import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "glitch_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const buildWsUrl = (sessionId) => {
  const token = getToken();
  // BACKEND_URL is https://host — convert to wss://host
  const u = new URL(BACKEND_URL);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}/api/ws/audio?token=${encodeURIComponent(
    token
  )}&session_id=${encodeURIComponent(sessionId)}`;
};

export const auth = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateMemory: (memory) =>
    api.patch("/user/memory", { memory }).then((r) => r.data),
  appendMemory: (content) =>
    api.post("/user/memory/append", { content }).then((r) => r.data),
  uploadAvatar: (file, label) => {
    const fd = new FormData();
    fd.append("file", file);
    if (label) fd.append("label", label);
    return api
      .post("/user/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  renameAvatar: (id, label) =>
    api.patch(`/user/avatar/${id}`, { label }).then((r) => r.data),
  deleteAvatarById: (id) =>
    api.delete(`/user/avatar/${id}`).then((r) => r.data),
  deleteAllAvatars: () => api.delete("/user/avatar").then((r) => r.data),
  updatePreferences: (payload) =>
    api.patch("/user/preferences", payload).then((r) => r.data),
  listVoices: () => api.get("/voices").then((r) => r.data),
  googleSession: (sessionId) =>
    api
      .post("/auth/google/session", null, {
        headers: { "X-Session-ID": sessionId },
      })
      .then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};

export const sessionsApi = {
  list: () => api.get("/sessions").then((r) => r.data),
  create: (title) => api.post("/sessions", { title }).then((r) => r.data),
  rename: (id, title) =>
    api.patch(`/sessions/${id}`, { title }).then((r) => r.data),
  remove: (id) => api.delete(`/sessions/${id}`).then((r) => r.data),
  messages: (id) => api.get(`/sessions/${id}/messages`).then((r) => r.data),
  appendMessage: (id, payload) =>
    api.post(`/sessions/${id}/messages`, payload).then((r) => r.data),
  chat: (id, text, attachment) =>
    api
      .post(`/sessions/${id}/chat`, {
        text,
        attachment: attachment ? { file_id: attachment.id } : null,
      })
      .then((r) => r.data),
  search: (q) =>
    api.get(`/search`, { params: { q } }).then((r) => r.data),
};

export const notesApi = {
  list: (project) =>
    api
      .get(`/notes`, { params: project ? { project } : {} })
      .then((r) => r.data),
  create: (payload) => api.post(`/notes`, payload).then((r) => r.data),
  update: (id, payload) =>
    api.patch(`/notes/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/notes/${id}`).then((r) => r.data),
};

export const mediaUrl = (fileUrl) => {
  if (!fileUrl) return "";
  if (/^https?:/i.test(fileUrl)) return fileUrl;
  return `${BACKEND_URL}${fileUrl}`;
};

export const generationsApi = {
  list: (kind) =>
    api
      .get(`/generations`, { params: kind ? { kind } : {} })
      .then((r) => r.data),
  createImage: (prompt, model, useAvatar, avatarId, aspectRatio, imageFileId) =>
    api
      .post(`/images`, {
        prompt,
        model,
        use_avatar: !!useAvatar,
        avatar_id: avatarId || null,
        aspect_ratio: aspectRatio || null,
        image_file_id: imageFileId || null,
      })
      .then((r) => r.data),
  createVideo: (prompt, duration_seconds = 6, model, useAvatar, avatarId, aspectRatio) =>
    api
      .post(`/videos`, {
        prompt,
        duration_seconds,
        model,
        use_avatar: !!useAvatar,
        avatar_id: avatarId || null,
        aspect_ratio: aspectRatio || null,
      })
      .then((r) => r.data),
  videoStatus: (id) =>
    api.get(`/videos/${id}/status`).then((r) => r.data),
  remove: (id) => api.delete(`/generations/${id}`).then((r) => r.data),
};

export const IMAGE_MODELS = [
  { value: "nano-banana", label: "Nano Banana (Google · fast)" },
  { value: "imagen4", label: "Imagen 4 (Google · high quality)" },
];
export const VIDEO_MODELS = [
  { value: "veo3-fast", label: "Veo 3 Fast (Google · with audio)" },
  { value: "veo3", label: "Veo 3 (Google · highest quality · with audio)" },
];
export const ASPECT_RATIOS = [
  { value: "9:16", label: "Portrait", short: "9:16" },
  { value: "1:1", label: "Square", short: "1:1" },
  { value: "16:9", label: "Landscape", short: "16:9" },
];

export const exportSession = (sessionId) => {
  // Build a URL that inherits the JWT via cookies — simplest across auth modes.
  const url = `${BACKEND_URL}/api/sessions/${sessionId}/export?format=md`;
  return api
    .get(`/sessions/${sessionId}/export`, {
      params: { format: "md" },
      responseType: "blob",
    })
    .then((r) => {
      const blob = new Blob([r.data], { type: "text/markdown" });
      const dispo = r.headers?.["content-disposition"] || "";
      const m = dispo.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : "conversation.md";
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
      URL.revokeObjectURL(dl.href);
      return { url, filename };
    });
};

export const promptsApi = {
  list: (kind) =>
    api.get(`/prompts`, { params: kind ? { kind } : {} }).then((r) => r.data),
  create: (payload) => api.post(`/prompts`, payload).then((r) => r.data),
  update: (id, payload) =>
    api.patch(`/prompts/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/prompts/${id}`).then((r) => r.data),
};

export const sttApi = {
  transcribe: (blob, filename = "recording.webm") => {
    const form = new FormData();
    form.append("audio", blob, filename);
    return api
      .post(`/transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
};

export const filesApi = {
  upload: (file, sessionId, onProgress) => {
    const form = new FormData();
    form.append("file", file, file.name);
    if (sessionId) form.append("session_id", sessionId);
    return api
      .post(`/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!onProgress || !evt.total) return;
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        // 250 MB uploads can take a while
        timeout: 15 * 60 * 1000,
      })
      .then((r) => r.data);
  },
  list: (sessionId) =>
    api
      .get(`/files`, { params: sessionId ? { session_id: sessionId } : {} })
      .then((r) => r.data),
  remove: (id) => api.delete(`/files/${id}`).then((r) => r.data),
  downloadUrl: (id) => `${API_BASE}/files/${id}/download`,
  fetchBlobUrl: async (id) => {
    const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
    return URL.createObjectURL(r.data);
  },
};
